import { Configuration, LogLevel } from '@azure/msal-browser'
import {
  ENV,
  GUID_PATTERN,
  IS_STAGING,
  activeTenantProfile,
  isPagesPreviewHost,
  isProductionSupportHost,
  isSandboxHost,
  isStagingHost,
  readEnv,
} from './tenantConfig'

/**
 * Orbit ITSM - MSAL Authentication Configuration
 * 
 * ENVIRONMENT SETUP:
 * - Development: Uses development/testing tenant
 * - Production: Uses production tenant
 * 
 * Set VITE_ENV=production in .env for production build
 */

// ============================================================
// ENVIRONMENT CONFIGURATION
// ============================================================

const configuredSharedMailbox = readEnv('VITE_SHARED_MAILBOX')
const configuredSharedMailboxName = readEnv('VITE_SHARED_MAILBOX_NAME')
const configuredApprovalEmailOverride = readEnv('VITE_APPROVAL_EMAIL_OVERRIDE')
const configuredApprovalEmailOverrideName = readEnv('VITE_APPROVAL_EMAIL_OVERRIDE_NAME')
const configuredItTeamGroupId = readEnv('VITE_IT_TEAM_GROUP_ID')
const configuredItApproversGroupId = readEnv('VITE_IT_APPROVERS_GROUP_ID')
const configuredItFinanceGroupId = readEnv('VITE_IT_FINANCE_GROUP_ID')
const configuredAllStaffGroupId = readEnv('VITE_ALL_STAFF_GROUP_ID')
const configuredOrbitLifecycleGroupId = readEnv('VITE_ORBIT_LIFECYCLE_GROUP_ID')

if (configuredApprovalEmailOverride && !configuredApprovalEmailOverride.includes('@')) {
  throw new Error('[Config] VITE_APPROVAL_EMAIL_OVERRIDE must be a valid email address when set')
}

const fallbackSandboxMailbox = configuredSharedMailbox
const fallbackSandboxName = configuredSharedMailboxName || 'Service Desk'

// Approval routing policy:
// - Production support host: ALWAYS live (IT Approvers group / standard routing)
// - Staging/preview Pages hosts: ALWAYS sandboxed (never manager mailbox)
// - Other hosts: use optional override if configured
const effectiveApprovalEmailOverride = isProductionSupportHost
  ? ''
  : (isSandboxHost
    ? (configuredApprovalEmailOverride || fallbackSandboxMailbox)
    : configuredApprovalEmailOverride)
const effectiveApprovalEmailOverrideName = effectiveApprovalEmailOverride
  ? (configuredApprovalEmailOverrideName || `${fallbackSandboxName} Sandbox`)
  : ''

function requireProductionEnv(
  key: string,
  value: string,
  message: string,
  validator?: (currentValue: string) => boolean,
  severity: 'warn' | 'error' = 'warn',
) {
  if (ENV !== 'production') return
  if (!value || (validator && !validator(value))) {
    const formattedMessage = `[Config] ${key} ${message}`
    if (severity === 'error') {
      throw new Error(formattedMessage)
    }
    console.warn(formattedMessage)
  }
}

requireProductionEnv('VITE_SHARED_MAILBOX', configuredSharedMailbox, 'is not configured with a valid production mailbox', (value) => {
  return value.includes('@') && !/example\.com$/i.test(value)
})
requireProductionEnv('VITE_SHARED_MAILBOX_NAME', configuredSharedMailboxName, 'is not configured for production')
requireProductionEnv('VITE_IT_TEAM_GROUP_ID', configuredItTeamGroupId, 'must be set to a valid Entra group GUID', (value) => GUID_PATTERN.test(value))
requireProductionEnv('VITE_IT_APPROVERS_GROUP_ID', configuredItApproversGroupId, 'must be set to a valid Entra group GUID', (value) => GUID_PATTERN.test(value))
requireProductionEnv('VITE_IT_FINANCE_GROUP_ID', configuredItFinanceGroupId, 'must be set to a valid Entra group GUID', (value) => GUID_PATTERN.test(value))

// Optional: people-picker enhancement only. Missing value should not block app startup.
if (configuredAllStaffGroupId && !GUID_PATTERN.test(configuredAllStaffGroupId)) {
  throw new Error('[Config] VITE_ALL_STAFF_GROUP_ID must be a valid Entra group GUID when provided')
}
if (ENV === 'production' && !configuredAllStaffGroupId) {
  console.warn('[Config] VITE_ALL_STAFF_GROUP_ID is not set — People Picker autocomplete will be disabled')
}

// Optional: gates the Staff Lifecycle (onboarding/offboarding) tab and watcher expansion.
if (configuredOrbitLifecycleGroupId && !GUID_PATTERN.test(configuredOrbitLifecycleGroupId)) {
  throw new Error('[Config] VITE_ORBIT_LIFECYCLE_GROUP_ID must be a valid Entra group GUID when provided')
}
if (ENV === 'production' && !configuredOrbitLifecycleGroupId) {
  console.warn('[Config] VITE_ORBIT_LIFECYCLE_GROUP_ID is not set — Staff Lifecycle tab will be hidden and watcher auto-expansion disabled')
}

console.log(`🔧 Service Desk running in ${ENV.toUpperCase()} mode${IS_STAGING ? ' (STAGING)' : ''}`)
console.log(`🏢 Tenant: ${activeTenantProfile.label}${IS_STAGING ? ' — staging mode' : ''}`)

// ============================================================
// MSAL CONFIGURATION
// ============================================================

export const msalConfig: Configuration = {
  auth: {
    clientId: activeTenantProfile.clientId,
    authority: `https://login.microsoftonline.com/${activeTenantProfile.tenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return
        switch (level) {
          case LogLevel.Error:
            console.error(message)
            break
          case LogLevel.Warning:
            console.warn(message)
            break
          case LogLevel.Info:
            console.info(message)
            break
          case LogLevel.Verbose:
            console.debug(message)
            break
        }
      },
      logLevel: ENV === 'development' ? LogLevel.Info : LogLevel.Warning,
    },
  },
}

// ============================================================
// GRAPH API SCOPES
// ============================================================

export const graphScopes = {
  // Read/write SharePoint lists via Graph API
  sharepoint: ['Sites.Read.All', 'Sites.ReadWrite.All'],
  // User profile + read group/user directory data (for IT approvers resolution)
  user: ['User.Read', 'User.Read.All'],
  // Send email as shared mailbox (used by admin Send Update + approval emails)
  mail: ['Mail.Send'],
}

// SharePoint REST API scope (for CAML queries)
// Extract base URL from siteUrl for proper scope
const sharepointHost = new URL(activeTenantProfile.siteUrl).origin
export const sharepointRestScopes = [`${sharepointHost}/.default`]

// ============================================================
// SHAREPOINT CONFIGURATION
// ============================================================

export const sharepointConfig = {
  siteUrl: activeTenantProfile.siteUrl,
  /** SharePoint Projects Hub — used to pull active tenders/projects for onboarding forms */
  projectsHubSiteUrl: readEnv('VITE_PROJECTS_HUB_SITE_URL') || '',
  lists: {
    tickets: 'IT-Tickets',
    comments: 'IT-Ticket-Comments',
    assets: 'IT-Assets',
    serviceCatalogue: 'IT-Service-Catalogue',
    approvals: 'IT-Approvals',
    portalUsage: 'IT-Portal-Usage',
    userLifecycle: 'IT-User-Lifecycle',
  },
}

// ============================================================
// NOTIFICATION CONFIGURATION
// ============================================================

export const notificationConfig = {
  /** Shared mailbox — all outbound support emails come from this address */
  sharedMailbox: configuredSharedMailbox,
  sharedMailboxName: configuredSharedMailboxName,

  /** Support portal URL */
  portalUrl: import.meta.env.VITE_PORTAL_URL || window.location.origin,

  /**
   * Optional staging/preview safety switch: if set, all approval request emails
   * are routed to this mailbox instead of the resolved manager.
   * Ignored on production support hostname.
   */
  approvalEmailOverride: effectiveApprovalEmailOverride,
  approvalEmailOverrideName: effectiveApprovalEmailOverrideName,

  /**
   * Teams Workflow webhook URL for posting adaptive cards to IT Service Desk channel.
   * Set up in Teams: Channel → ... → Workflows → "Post to a channel when a webhook request is received"
   * Paste the generated URL here or in VITE_TEAMS_WEBHOOK_URL env var.
   */
  teamsWebhookUrl: import.meta.env.VITE_TEAMS_WEBHOOK_URL || '',

  /** IT admin Entra group ID — used to show /admin link in nav */
  itTeamGroupId: configuredItTeamGroupId,

  /** IT Approvers Entra group ID — receives approval requests, has SP read access */
  itApproversGroupId: configuredItApproversGroupId,

  /** IT Finance Users Entra group ID — controls finance asset/procurement visibility */
  itFinanceGroupId: configuredItFinanceGroupId,

  /** All Staff Entra group ID — used for people-picker autocomplete */
  allStaffGroupId: configuredAllStaffGroupId,

  /** Staff Lifecycle Entra group ID — controls Staff Lifecycle tab visibility
   * and is auto-expanded into the Watchers field on every onboarding/offboarding
   * ticket created via the catalogue (SP-001 / SP-003). */
  orbitLifecycleGroupId: configuredOrbitLifecycleGroupId,
}

// Export for debugging
export const currentEnvironment = {
  env: ENV,
  isStaging: IS_STAGING || isStagingHost,
  tenant: activeTenantProfile.shortName,
  tenantLabel: activeTenantProfile.label,
  tenantDomain: activeTenantProfile.tenantDomain,
  tenantId: activeTenantProfile.tenantId,
  clientId: activeTenantProfile.clientId,
  siteUrl: activeTenantProfile.siteUrl,
  approvalEmailOverrideActive: !!effectiveApprovalEmailOverride,
  approvalRoutingMode: isProductionSupportHost ? 'live' : (effectiveApprovalEmailOverride ? 'sandbox' : 'live'),
  isPagesPreviewHost,
}
